import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { useAuth, useLang } from "../lib/context";
import { Card, Btn, PageHead } from "../components/Shell";
import { PiggyBank, Building2, BadgeCheck, RotateCcw, Search, ArrowRight, Info } from "lucide-react";
import { toast } from "sonner";

const JOURNEYS = [
  ["savings", "Open a Savings Account", "Personal banking journey. Does not enable merchant products.", PiggyBank, "/start/savings", "bg-[#6D28D9] text-white"],
  ["current", "Open a Current Account", "For businesses - we ask your entity type first.", Building2, "/start/current", "bg-[#FF5A5F] text-white"],
  ["existing", "I Already Have a Current Account", "Verify your account, then apply for PG, QR or POS.", BadgeCheck, "/start/existing_account", "bg-[#0EA5A4] text-white"],
  ["resume", "Resume an Existing Application", "Your drafts are saved on our server.", RotateCcw, "/resume", "bg-[#C6F432] text-[#0E0B1F]"],
  ["track", "Track Application Status", "Look up by application number.", Search, "/resume#track", "bg-[#FFB020] text-[#0E0B1F]"],
];

export default function Home() {
  const { t } = useLang();
  const { user } = useAuth();
  return (
    <div className="space-y-8">
      <section className="sx-grad relative overflow-hidden rounded-[2rem] p-8 text-white shadow-2xl shadow-fuchsia-600/20 sm:p-12">
        <div className="pointer-events-none absolute -right-20 -top-24 h-80 w-80 rounded-full bg-[#C6F432]/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 left-1/4 h-72 w-72 rounded-full bg-[#0E0B1F]/40 blur-3xl" />
        <div className="relative grid items-center gap-8 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/15 px-3 py-1 text-xs font-semibold text-white backdrop-blur"><span className="h-1.5 w-1.5 rounded-full bg-[#C6F432]" /> Bank accounts · PG · QR · POS</span>
            <h1 data-testid="home-title" className="mt-5 text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">Namaste, <span className="text-[#C6F432]">{user.name?.split(" ")[0] || ""}</span></h1>
            <p className="mt-3 max-w-lg text-base text-white/80">Enter your details once, open your bank account and activate payment products - every step tracked separately and honestly.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/start/current" className="inline-flex min-h-[48px] items-center gap-2 rounded-full bg-[#0E0B1F] px-6 text-sm font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5">Start business onboarding <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/merchant" className="inline-flex min-h-[48px] items-center rounded-full border border-white/30 bg-white/15 px-6 text-sm font-semibold backdrop-blur transition-colors hover:bg-white/20">View dashboard</Link>
            </div>
          </div>
          <div className="relative hidden h-56 lg:block">
            <div className="floaty absolute right-6 top-0 w-64 rounded-2xl bg-white p-4 text-[#0E0B1F] shadow-2xl">
              <div className="text-xs text-slate-500">Current Account</div><div className="mt-1 flex items-center gap-2 text-sm font-bold"><span className="h-2 w-2 rounded-full bg-emerald-400" /> Active</div>
              <div className="mt-3 h-1.5 rounded-full bg-slate-100"><div className="h-full w-full rounded-full bg-[#C6F432]" /></div>
            </div>
            <div className="floaty absolute bottom-0 left-0 w-60 rounded-2xl bg-[#0E0B1F] p-4 shadow-2xl" style={{ animationDelay: "1.5s" }}>
              <div className="text-xs text-slate-400">Payment Gateway</div><div className="mt-1 flex items-center gap-2 text-sm font-bold"><span className="h-2 w-2 rounded-full bg-amber-400" /> Provider review</div>
              <div className="mt-3 h-1.5 rounded-full bg-white/10"><div className="h-full w-2/3 rounded-full bg-gradient-to-r from-amber-400 to-orange-400" /></div>
            </div>
          </div>
        </div>
      </section>
      <div>
        <h2 className="mb-5 text-2xl font-extrabold text-[#0E0B1F]">What would you like to do?</h2>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {JOURNEYS.map(([k, title, sub, Icon, to, grad], i) => (
            <Link key={k} to={to} data-testid={`journey-${k}`} style={{ animationDelay: `${i * 70}ms` }}
              className={`group fade-up relative flex min-h-[200px] flex-col overflow-hidden rounded-3xl p-6 shadow-lg transition-transform duration-300 hover:-translate-y-1.5 hover:shadow-2xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-400 ${grad}`}>
              <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/20 transition-transform duration-500 group-hover:scale-125" />
              <span className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-white/25 backdrop-blur"><Icon className="h-6 w-6" /></span>
              <div className="font-display relative mt-5 text-xl font-extrabold">{t(title)}</div>
              <div className="relative mt-1 flex-1 text-sm opacity-80">{sub}</div>
              <span className="relative mt-5 inline-flex w-fit items-center gap-1 rounded-full bg-white/25 px-4 py-1.5 text-sm font-bold backdrop-blur">Start <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function StartJourney() {
  const { kind } = useParams();
  const { config, user } = useAuth();
  const nav = useNavigate();
  const [entity, setEntity] = useState(kind === "savings" ? "individual" : "");
  const [residency, setResidency] = useState("resident");
  const [provider, setProvider] = useState(kind === "existing_account" ? "sandbox_pg" : "");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  if (!config) return null;
  const product = kind === "existing_account" ? null : kind;
  const banks = config.providers.filter((p) => p.kind === "bank");
  const elig = (b) => {
    let m = config.eligibility[b]?.[product]?.[entity] || "unavailable";
    if (residency !== "resident" && m === "api") m = "assisted";
    return m;
  };
  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post("/applications", { kind, provider, entity_type: entity, residency, customer_email: user.role === "field_agent" ? email : undefined });
      toast.success(`Application ${r.data.app_number} created`);
      nav(`/applications/${r.data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const assisted = async (b) => {
    try {
      const r = await api.post("/assisted-requests", { provider: b, product, entity_type: entity });
      toast.success(`Assisted review requested (${r.data.reference}). Our team will contact you.`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const MODE = { api: ["Digital journey", "bg-emerald-50 text-emerald-800 border-emerald-300"], assisted: ["Assisted route", "bg-purple-50 text-purple-800 border-purple-300"], unavailable: ["Not available", "bg-slate-100 text-slate-600 border-slate-300"] };
  return (
    <div className="space-y-6">
      <PageHead testid="start-title" title={{ savings: "Open a Savings Account", current: "Open a Current Account", existing_account: "Verify your existing current account" }[kind]} sub="Eligibility comes from each bank's configured product rules." />
      {kind !== "savings" && (
        <Card>
          <h2 className="mb-4 flex items-center gap-3 font-bold"><span className="flex h-7 w-7 items-center justify-center rounded-full sx-grad text-xs text-white">1</span> What type of entity is the business?</h2>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {config.entity_types.filter((e) => kind === "current" || e.code !== "individual").map((e) => (
              <button key={e.code} data-testid={`entity-${e.code}`} onClick={() => setEntity(e.code)} className={`min-h-[52px] rounded-xl border px-4 py-3 text-left text-sm font-semibold transition-all duration-200 active:scale-[0.98] ${entity === e.code ? "border-transparent sx-grad text-white shadow-lg shadow-indigo-500/25" : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-md"}`}>{e.label}</button>
            ))}
          </div>
        </Card>
      )}
      {kind === "savings" && (
        <Card>
          <h2 className="mb-4 flex items-center gap-3 font-bold"><span className="flex h-7 w-7 items-center justify-center rounded-full sx-grad text-xs text-white">1</span> Residency</h2>
          <select data-testid="residency-select" className="fx-input min-h-[48px] rounded-xl border border-slate-300 px-4 text-sm" value={residency} onChange={(e) => setResidency(e.target.value)}>
            <option value="resident">Resident Indian</option><option value="nri">Non-Resident Indian</option><option value="foreign">Foreign national</option>
          </select>
        </Card>
      )}
      {kind !== "existing_account" && entity && (
        <Card>
          <h2 className="mb-4 flex items-center gap-3 font-bold"><span className="flex h-7 w-7 items-center justify-center rounded-full sx-grad text-xs text-white">2</span> Choose a bank</h2>
          <div className="space-y-2">
            {banks.map((b) => {
              const m = elig(b.code);
              return (
                <div key={b.code} className={`flex flex-wrap items-center gap-3 rounded-2xl border p-5 transition-all duration-200 ${provider === b.code ? "border-indigo-400 bg-indigo-50/60 shadow-md ring-2 ring-indigo-500/20" : "border-slate-200 bg-white hover:border-indigo-200"}`} data-testid={`bank-${b.code}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-slate-800 to-indigo-900 text-sm font-bold text-white">{b.name[0]}</span>
                  <div className="flex-1">
                    <div className="font-semibold">{b.name}</div>
                    {config.eligibility_notes[b.code] && <div className="mt-1 flex gap-1 text-xs text-slate-500"><Info className="h-3 w-3 shrink-0" />{config.eligibility_notes[b.code]}</div>}
                  </div>
                  <span data-testid={`eligibility-${b.code}`} className={`rounded-full border px-3 py-1 text-xs font-semibold ${MODE[m][1]}`}>{MODE[m][0]}{b.mode === "sandbox" ? " (sandbox)" : b.mode === "api_blocked" && m === "api" ? " - API pending" : ""}</span>
                  {m === "unavailable" ? <Btn variant="outline" data-testid={`assisted-request-${b.code}`} onClick={() => assisted(b.code)}>Request assisted review</Btn>
                    : <Btn variant={provider === b.code ? "primary" : "outline"} data-testid={`select-bank-${b.code}`} onClick={() => setProvider(b.code)}>{provider === b.code ? "Selected" : "Select"}</Btn>}
                </div>
              );
            })}
          </div>
        </Card>
      )}
      {user.role === "field_agent" && (
        <Card>
          <label className="text-sm font-medium" htmlFor="cust-email">Customer's email (they will sign in to review and consent)</label>
          <input id="cust-email" data-testid="customer-email-input" className="mt-1 min-h-[44px] w-full rounded-lg border border-slate-300 px-3 text-sm" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Card>
      )}
      <Btn data-testid="start-application-btn" disabled={busy || !entity || !provider || (user.role === "field_agent" && !email)} onClick={create}>Start application <ArrowRight className="h-4 w-4" /></Btn>
    </div>
  );
}
